import { useQuery, useQueryClient } from "@tanstack/react-query";
import { custom, z } from "zod";
import { Decimal } from "decimal.js";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
  MimeType,
} from "../../utils";
import { PossibleRole } from "lhc_permissions";
import { transformToDecimal } from "../../common";
import { BaseDbObject } from "@/lib/models/db";
import {
  AcceptedCommodity,
  AcceptedCommodityType,
  acceptedCommodityTypes,
  AcceptedCommoditiesValidation,
} from "./commodities";

export {
  AcceptedCommodity,
  AcceptedCommodityType,
  AcceptedCommoditiesValidation,
  acceptedCommodityTypes,
  cropProfileAcceptedCommodityTypes,
} from "./commodities";


export const GeneralFiltersValidation = z.object({
  is_enabled: z.boolean().default(true),
  crop_year: z.array(z.number().int().positive().gte(2000)).optional(),
  commodities: z.array(AcceptedCommoditiesValidation).optional(),
  trader_id: z.string().uuid().optional(),
  customer_id: z.string().uuid().optional(),
});

export const CustomersCropProfileOptions = z.object({
  filters: GeneralFiltersValidation.optional()
});

export const CustomerCropProfileOptions = z.object({
  filters: GeneralFiltersValidation.optional()
});

export const CreateCustomerCropProfileValidation = z.object({
  customer_id: z.string().uuid(),
  commodity_name: AcceptedCommoditiesValidation,
  crop_year: z.number().int().gte(2000),
  acreage: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "acreage" })),
  projected_yield: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "projected_yield" })),
  actual_yield: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "actual_yield" })),

  cost_per_acre: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "cost_per_acre" })),
});

// NOTE: DO not allow changing customer in the normal update route, make user use a specific route
export const UpdateCustomerCropProfileValidation = z.object({
  is_enabled: z.boolean(),

  commodity_name: AcceptedCommoditiesValidation,
  crop_year: z.number().int().gte(2000),

  acreage: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "acreage" })),
  projected_yield: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "projected_yield" })),
  actual_yield: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "actual_yield" })),

  cost_per_acre: (z.number().or(z.string())).optional().transform(transformToDecimal({ throwOnInvalid: true, key: "cost_per_acre" })),
});

export const GenerateCropProfileReportValidation = z.object({
  filters: GeneralFiltersValidation.optional(),
  customer_id: z.string().uuid().optional(),
})

export const UploadReportValidation = z.object({
  filename: z.string(),
  mime_type: z.enum([MimeType.Xlsx]),
})

export interface RiskCropProfile extends BaseDbObject {
  customer_id: string;
  commodity_name: string;
  commodity_type: string;
  crop_year: number;
  acreage: Decimal | null;
  projected_yield: Decimal | null;
  actual_yield: Decimal | null;
  actual_total_production: Decimal | null;
  projected_total_production: Decimal | null;
  cost_per_acre: Decimal | null;
  is_enabled: boolean;
  Customer: {
    client_id: string;
    long_name: string;
  }
}

export interface RiskCropProfileHistory extends RiskCropProfile {
  profile_id: string;
  user_id: string;
  history_type: "created" | "updated";

  User?: { name: string }
}

export interface RiskManagementCustomerProfile {
  profiles: RiskCropProfile[];
  histories: RiskCropProfileHistory[];
  summaries: {
    customer: {
      total_actual_production: string;
      total_projected_production: string;
    }
  }
}

interface ProductionSummary {
  total_projected_production: string;
  total_actual_production: string;
}

type UomSummaryBreakdown = {
  total_projected_production: string;
  total_actual_production: string;
  by_year: Record<string, {
    total_projected_production: string;
    total_actual_production: string;
    by_commodity: Record<string, ProductionSummary>;
  }>;
};

export interface TopLevelResult {
  profiles: RiskCropProfile[];
  histories: RiskCropProfileHistory[];
  summaries: {
    commodity_summaries: Record<string, UomSummaryBreakdown>,
    customer_summaries: Record<string, UomSummaryBreakdown>,
    customers: Record<string, {
      customer_id: string;
      customer_name: string;
      uom_summaries: Record<string, UomSummaryBreakdown>
    }>;
    uom_summaries: Record<string, UomSummaryBreakdown>
  }
}

export interface CashUploadReportResult {
  link: string;
  document_id: string;
  job_id: string;
}

export function useListCustomersTopLevel({ filters }: { filters?: z.infer<typeof GeneralFiltersValidation> }) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.crop_year && filters.crop_year?.length > 0) {
      filters.crop_year.forEach(crop_year => queryParams.append("crop_year", crop_year.toString()));
    }
    if (filters.commodities && filters.commodities.length > 0) {
      filters.commodities.forEach(commodity => queryParams.append("commodities", commodity));
    }
    if (filters.is_enabled !== undefined) {
      queryParams.set("is_enabled", filters.is_enabled.toString());
    }
    if (filters.trader_id) {
      queryParams.set("trader_id", filters.trader_id);
    }
    if (filters.customer_id) {
      queryParams.set("customer_id", filters.customer_id);
    }
  }

  return useQuery({
    queryKey: ["risk", "profiles", "top_level", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<TopLevelResult>("risk/management/crop_profiles", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useListCustomerProfiles({ customer_id, filters }: { customer_id?: string; filters?: z.infer<typeof GeneralFiltersValidation> }) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.crop_year && filters.crop_year?.length > 0) {
      filters.crop_year.forEach(crop_year => queryParams.append("crop_year", crop_year.toString()));
    }
    if (filters.commodities && filters.commodities.length > 0) {
      filters.commodities.forEach(commodity => queryParams.append("commodities", commodity));
    }
    if (filters.is_enabled !== undefined) {
      queryParams.set("is_enabled", filters.is_enabled.toString());
    }
  }

  return useQuery({
    queryKey: ["risk", "profiles", "customer", customer_id, queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementCustomerProfile>(`risk/management/crop_profiles/customer/${customer_id}`, queryParams),
    retry: 2,
    enabled: !!customer_id,
    keepPreviousData: true
  });
}

export function useCreateProfile() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateCustomerCropProfileValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `risk/management/crop_profiles/profile`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return async (profile_id: string, data: z.infer<typeof UpdateCustomerCropProfileValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `risk/management/crop_profiles/profile/${profile_id}`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useGenerateProfileReport() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof GenerateCropProfileReportValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `risk/management/crop_profiles/report`,
      data,
    );

    return result;
  };
}

export function useGenerateCashReportUpload() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof UploadReportValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<CashUploadReportResult>(
      `risk/management/cash_sales/upload`,
      data,
    );

    return result;
  };
}
